/**
 * Outil « Multiples et nombres premiers » — calculs (pur).
 *
 * Grille de 1 à 100 : multiples d'un entier, diviseurs d'une case, crible
 * d'Ératosthène pas à pas ; décomposition en facteurs premiers ; PGCD par
 * l'algorithme d'Euclide (et le rectangle découpé en carrés qui l'illustre).
 */

export const GRID_MAX = 100;

export function divisors(n: number): number[] {
  const out: number[] = [];
  for (let d = 1; d <= n; d++) if (n % d === 0) out.push(d);
  return out;
}

export function isPrime(n: number): boolean {
  if (n < 2) return false;
  for (let d = 2; d * d <= n; d++) if (n % d === 0) return false;
  return true;
}

/**
 * Crible d'Ératosthène jusqu'à `max`, par étapes : à l'étape k, on a barré
 * les multiples (sauf eux-mêmes) des k premiers nombres premiers. Une étape
 * de plus après √max ne barre plus rien : le crible est fini.
 */
export interface SieveState {
  /** nombres premiers dont on a barré les multiples */
  done: number[];
  /** barré par quel premier (0 : pas barré) */
  crossedBy: number[];
  /** le prochain premier à traiter, ou null si le crible est fini */
  next: number | null;
}

export function sieve(max: number, steps: number): SieveState {
  const crossedBy = Array(max + 1).fill(0) as number[];
  const done: number[] = [];
  let p = 2;
  for (let s = 0; s < steps && p * p <= max; s++) {
    for (let m = p * p; m <= max; m += p) if (!crossedBy[m]) crossedBy[m] = p;
    done.push(p);
    p++;
    while (p <= max && crossedBy[p]) p++;
  }
  return { done, crossedBy, next: p * p <= max ? p : null };
}

/** Décomposition en facteurs premiers : étapes de l'arbre et forme finale. */
export function factorize(n: number): {
  factors: number[];
  steps: Array<[number, number, number]>;
} {
  const factors: number[] = [];
  const steps: Array<[number, number, number]> = [];
  let m = n;
  let d = 2;
  while (m > 1 && d * d <= m) {
    if (m % d === 0) {
      steps.push([m, d, m / d]);
      factors.push(d);
      m /= d;
    } else d++;
  }
  if (m > 1) factors.push(m);
  return { factors, steps };
}

/** 2² × 3 × 7 */
export function powerForm(factors: readonly number[]): string {
  const SUP = '⁰¹²³⁴⁵⁶⁷⁸⁹';
  const counts = new Map<number, number>();
  for (const f of factors) counts.set(f, (counts.get(f) ?? 0) + 1);
  return [...counts.entries()]
    .map(([p, k]) =>
      k === 1
        ? String(p)
        : `${p}${String(k)
            .split('')
            .map((c) => SUP[Number(c)])
            .join('')}`,
    )
    .join(' × ');
}

/** Divisions de l'algorithme d'Euclide : a = b × q + r, jusqu'au reste nul. */
export function euclid(
  a: number,
  b: number,
): Array<{ a: number; b: number; q: number; r: number }> {
  const out: Array<{ a: number; b: number; q: number; r: number }> = [];
  let [x, y] = a >= b ? [a, b] : [b, a];
  while (y > 0) {
    const q = Math.floor(x / y);
    const r = x % y;
    out.push({ a: x, b: y, q, r });
    [x, y] = [y, r];
  }
  return out;
}

export function gcd(a: number, b: number): number {
  const steps = euclid(a, b);
  return steps.length ? steps[steps.length - 1]!.b : Math.max(a, b);
}

/**
 * Carrés du rectangle a × b découpé selon Euclide : à chaque étape, q carrés
 * de côté b ; le dernier carré a pour côté le PGCD.
 */
export function euclidSquares(
  a: number,
  b: number,
): Array<{ x: number; y: number; side: number; step: number }> {
  const out: Array<{ x: number; y: number; side: number; step: number }> = [];
  let x0 = 0;
  let y0 = 0;
  let w = Math.max(a, b);
  let h = Math.min(a, b);
  let horizontal = true; // les carrés s'alignent le long de la longueur
  let step = 0;
  while (w > 0 && h > 0) {
    const q = Math.floor(w / h);
    for (let i = 0; i < q; i++) {
      out.push(
        horizontal
          ? { x: x0 + i * h, y: y0, side: h, step }
          : { x: x0, y: y0 + i * h, side: h, step },
      );
    }
    const r = w - q * h;
    if (horizontal) x0 += q * h;
    else y0 += q * h;
    [w, h] = [h, r];
    horizontal = !horizontal;
    step++;
  }
  return out;
}

/**
 * Bouts pour la feuille de brouillon : « 2² » = ['2', '²'] (la touche carré),
 * « 2³ », « 2¹⁰ » = un seul bloc (le moteur lit l'exposant collé au nombre).
 */
export function powerAtoms(factors: readonly number[]): string[] {
  const SUP = '⁰¹²³⁴⁵⁶⁷⁸⁹';
  const counts = new Map<number, number>();
  for (const f of factors) counts.set(f, (counts.get(f) ?? 0) + 1);
  const out: string[] = [];
  for (const [p, k] of counts) {
    if (out.length) out.push('×');
    if (k === 1) out.push(String(p));
    else if (k === 2) out.push(String(p), '²');
    else
      out.push(
        `${p}${String(k)
          .split('')
          .map((c) => SUP[Number(c)])
          .join('')}`,
      );
  }
  return out;
}

/** Facteurs premiers communs (avec multiplicité) de deux décompositions. */
export function commonFactors(a: readonly number[], b: readonly number[]): number[] {
  const rest = [...b];
  const out: number[] = [];
  for (const f of a) {
    const i = rest.indexOf(f);
    if (i >= 0) {
      out.push(f);
      rest.splice(i, 1);
    }
  }
  return out;
}
